import { removeElement } from '../helpers';

export default {
  props: {
    message: String,

    /**
     * Declare the duration of the Toast (in milliseconds)
     */
    duration: {
      type: Number,
      default: 3500,
    },

    /**
     * Any color is available as long as it is declared at the configuration and tailwind level.
     */
    color: {
      type: String,
      default: 'primary-500',
      validator: val => !val.includes('#')
    },

    /**
     * If should queue with others notices (snackbar/toast/notification). Queues with other toasts by default.
     */
    queue: {
      type: Boolean,
      default: undefined,
    },

    /**
     * Position the Toast will appear on screen.
     * `top-right, top, top-left, bottom-right, bottom, bottom-left`
     */
    position: {
      type: String,
      default: 'bottom-right',
      validator(value) {
        return [
          'top-right',
          'top',
          'top-left',
          'bottom-right',
          'bottom',
          'bottom-left',
        ].indexOf(value) > -1;
      },
    },

    /**
     * Define the container of the event. This is only needed if the window isn't the relative object. (Typically just use `this` when called programmatically, or use a `$refs`)
     */
    container: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isActive: false,
      parentTop: null,
      parentBottom: null,
      newContainer: this.container || 'body',
    };
  },
  computed: {
    correctParent() {
      switch (this.position) {
        case 'top-right':
        case 'top':
        case 'top-left':
          return this.parentTop;

        case 'bottom-right':
        case 'bottom':
        case 'bottom-left':
          return this.parentBottom;
        default:
      }
    },

    transition() {
      switch (this.position) {
        case 'top-right':
        case 'top':
          return {
            enter: 'fadeInDown',
            leave: 'fadeOut',
          };
        case 'top-left':
          return {
            enter: 'fadeInDown',
            leave: 'fadeOut',
          };
        case 'bottom-right':
        case 'bottom':
        case 'bottom-left':
          return {
            enter: 'fadeInUp',
            leave: 'fadeOut',
          };
        default:
      }
    },
  },
  methods: {
    shouldQueue() {
      const queue = this.queue !== undefined
        ? this.queue
        : true;

      if (!queue) return false;

      return (
        this.parentTop.childElementCount > 0
        || this.parentBottom.childElementCount > 0
      );
    },

    close() {
      clearTimeout(this.timer);
      this.isActive = false;

      // Timeout for the animation complete before destroying
      setTimeout(() => {
        this.$destroy();
        removeElement(this.$el);
      }, 150);
    },

    showNotice() {
      if (this.shouldQueue()) {
        // Call recursively if should queue
        setTimeout(() => this.showNotice(), 250);
        return;
      }
      this.correctParent.insertAdjacentElement('afterbegin', this.$el);
      this.isActive = true;

      if (!this.indefinite) {
        this.timer = setTimeout(() => this.close(), this.newDuration);
      }
    },

    setupContainer() {
      this.parentTop = document.querySelector('.notices.top');
      this.parentBottom = document.querySelector('.notices.bottom');

      if (this.parentTop && this.parentBottom) return;

      if (!this.parentTop) {
        this.parentTop = document.createElement('div');
        this.parentTop.className = 'notices top';
      }

      if (!this.parentBottom) {
        this.parentBottom = document.createElement('div');
        this.parentBottom.className = 'notices bottom';
      }

      const container = document.querySelector(this.newContainer) || document.body;

      container.appendChild(this.parentTop);
      container.appendChild(this.parentBottom);

      if (this.newContainer && this.newContainer !== 'body') {
        this.parentTop.classList.add('has-custom-container');
        this.parentBottom.classList.add('has-custom-container');
      }
    },
  },
  beforeMount() {
    this.setupContainer();
  },
  mounted() {
    this.showNotice();
  },
};